import { fetchApi, trackError } from '@vend/utilities'

import { parseProxyErrorResponse } from './parseProxyErrorResponse'

export const TERMINAL_SAVE_SESSION_STATUS = {
  IN_PROGRESS: 'in_progress',
  REQUIRES_ACTION: 'requires_action',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const

export type TerminalSaveSessionStatus = typeof TERMINAL_SAVE_SESSION_STATUS[keyof typeof TERMINAL_SAVE_SESSION_STATUS]

export interface CreateTerminalSaveSessionParams {
  merchantReference: string
  customerReference: string
  pspTerminalId: string
  pli: string
  saleId?: string
  createdBy?: string
  metadata?: Record<string, string>
}

export interface CreateTerminalSaveSessionResponse {
  saveSessionId: string
  status: TerminalSaveSessionStatus
}

export interface GetTerminalSaveSessionParams {
  merchantReference: string
  customerReference: string
  saveSessionId: string
  pli: string
}

export interface CancelTerminalSaveSessionParams {
  merchantReference: string
  customerReference: string
  saveSessionId: string
  pli: string
}

export interface GetTerminalSaveSessionResponse {
  saveSessionId: string
  status: TerminalSaveSessionStatus
  failureReason?: string
}

/**
 * Create a terminal save session to save a card on file via terminal
 */
export const createTerminalSaveSession = async ({
  merchantReference,
  customerReference,
  pspTerminalId,
  pli,
  saleId,
  createdBy,
  metadata,
}: CreateTerminalSaveSessionParams): Promise<CreateTerminalSaveSessionResponse> => {
  try {
    const response = await fetchApi(
      `/api/2.0/lspay/processing/v1/merchants/${merchantReference}/customers/${customerReference}/payment-methods/terminal-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-lspay-pli': pli,
        },
        body: JSON.stringify({
          pspTerminalId,
          context: 'sale',
          ...(saleId && { saleId }),
          ...(createdBy && { createdBy }),
          ...(metadata && { metadata }),
        }),
      }
    )

    if (!response.ok) {
      const errorBody = await response.json()
      throw parseProxyErrorResponse(errorBody)
    }

    return response.json()
  } catch (error) {
    trackError(
      '[LSPayTerminalCardSave] Failed to create terminal save session',
      {
        data: {
          error,
          merchantReference,
          customerReference,
        },
      }
    )
    throw error
  }
}

/**
 * Cancel a terminal save session
 */
export const cancelTerminalSaveSession = async ({
  merchantReference,
  customerReference,
  saveSessionId,
  pli,
}: CancelTerminalSaveSessionParams): Promise<void> => {
  try {
    const response = await fetchApi(
      `/api/2.0/lspay/processing/v1/merchants/${merchantReference}/customers/${customerReference}/payment-methods/terminal-session/${saveSessionId}/cancel`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-lspay-pli': pli,
        },
      }
    )

    if (!response.ok) {
      const errorBody = await response.json()
      throw parseProxyErrorResponse(errorBody)
    }
  } catch (error) {
    trackError(
      '[LSPayTerminalCardSave] Failed to cancel terminal save session',
      {
        data: {
          error,
          merchantReference,
          customerReference,
          saveSessionId,
        },
      }
    )
    throw error
  }
}

/**
 * Get the status of a terminal save session (for polling)
 */
export const getTerminalSaveSession = async ({
  merchantReference,
  customerReference,
  saveSessionId,
  pli,
}: GetTerminalSaveSessionParams): Promise<GetTerminalSaveSessionResponse> => {
  try {
    const response = await fetchApi(
      `/api/2.0/lspay/processing/v1/merchants/${merchantReference}/customers/${customerReference}/payment-methods/terminal-session/${saveSessionId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-lspay-pli': pli,
        },
      }
    )

    if (!response.ok) {
      const errorBody = await response.json()
      throw parseProxyErrorResponse(errorBody)
    }

    return response.json()
  } catch (error) {
    trackError(
      '[LSPayTerminalCardSave] Failed to get terminal save session status',
      {
        data: {
          error,
          merchantReference,
          customerReference,
          saveSessionId,
        },
      }
    )
    throw error
  }
}
